import type { MessageDTO } from '@clanfire/shared';
import { threadPath, useThreads } from '@/stores/threads';

/**
 * Lets non-React code (socket handlers, notification clicks) navigate and know
 * what the user is looking at. AppLayout registers the router's navigate fn.
 */
let navigateFn: ((path: string) => void) | null = null;
/** Chats on screen (a channel and a thread can be open side by side), most recent last. */
const activeTargets: string[] = [];

export function registerNavigator(fn: ((path: string) => void) | null) {
  navigateFn = fn;
}

export function navigateTo(path: string) {
  navigateFn?.(path);
}

/** Marks a chat as on screen; returns a function that takes it off again. */
export function addActiveTarget(key: string): () => void {
  activeTargets.push(key);
  return () => {
    const i = activeTargets.lastIndexOf(key);
    if (i >= 0) activeTargets.splice(i, 1);
  };
}

/** The chat most recently opened (for keyboard shortcuts like "mark as read"). */
export function getActiveTarget(): string | null {
  return activeTargets.at(-1) ?? null;
}

export function isActiveTarget(key: string): boolean {
  return activeTargets.includes(key);
}

export function isWindowFocused() {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/**
 * Where a message lives. A thread's messages open beside its channel; for a
 * thread the client hasn't seen yet, the server page looks it up from its id.
 */
export function pathForMessage(m: Pick<MessageDTO, 'serverId' | 'channelId' | 'conversationId'>) {
  if (m.conversationId) return `/app/@me/${m.conversationId}`;
  const thread = m.channelId ? useThreads.getState().threads[m.channelId] : undefined;
  if (thread) return threadPath(thread);
  return `/app/${m.serverId}/${m.channelId}`;
}
